/**
 * AgentConfigSheet — Full-width slide-over for configuring Agent nodes
 *
 * Opens as a Sheet (700px wide) instead of the narrow property panel.
 * Two-column layout: Settings (left) + Resources (right with tabs + search).
 * Resources: Tools (searchable), Skills (searchable), Knowledge (Library browser with docs/folders/upload).
 * Memory: model-aware context window with limits per model.
 */

import { useState, useMemo } from 'react';
import {
  Bot, Wrench, BookOpen, Database, Search, X, Check,
  Brain, Shield, FileText, Sparkles, Settings2,
  ChevronDown, Folder, Globe, Upload, Cloud, Plus,
  File, StickyNote, FolderOpen,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { Link } from '@tanstack/react-router';
import { useWorkspaceTools } from '@/lib/use-workspace-tools';
import { useStudioSkills } from '@/lib/use-studio-skills';
import {
  isStudioSkillSelected,
  normalizeStudioSkillSelections,
  toggleStudioSkillSelection,
  type StudioSkillSelectionSource,
} from '@/lib/studio-skill-selection';
import { PromptApplyDialog } from '@/components/studio/prompts/prompt-apply-dialog';

// ── Model definitions with context window limits ────────────────────────────

const MODELS = [
  { id: 'default', name: 'Argus Default', detail: 'Qwen 2.5 72B AWQ', contextWindow: 32768, maxContext: 32768 },
  { id: 'fast', name: 'Argus Fast', detail: 'Llama 3.2 3B', contextWindow: 8192, maxContext: 8192 },
  { id: 'code', name: 'Argus Code', detail: 'Qwen Coder 32B', contextWindow: 32768, maxContext: 32768 },
  { id: 'precise', name: 'Argus Precise', detail: 'Qwen 72B, temp 0.1', contextWindow: 32768, maxContext: 32768 },
  { id: 'large', name: 'Argus Large Context', detail: 'Qwen 72B, extended', contextWindow: 131072, maxContext: 131072 },
  { id: 'max', name: 'Argus Max', detail: '1M context window', contextWindow: 1000000, maxContext: 1000000 },
];

function formatTokens(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(n % 1000000 === 0 ? 0 : 1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
  return String(n);
}

// ── Tool item shape (sourced from /workspace/tools) ─────────────────────────
//
// Tools available to the agent are read live from the workspace tools registry
// (built-in + MCP + OpenAPI + custom + database). The previously-hardcoded
// `AVAILABLE_TOOLS` array is gone — tenants without configured connections see
// an empty state with a link to /connections-module/mcp.

interface AgentToolItem {
  id: string;
  name: string;
  description: string;
  group: string;
}

interface AgentSkillItem {
  id: string;
  slug: string;
  name: string;
  description: string;
  scope: 'tenant' | 'system';
  category: string;
  tools: string[];
}

// #172 — the previous fabricated `LIBRARY_FOLDERS` (`All Documents`
// count 67) and `LIBRARY_DOCUMENTS` ("Pump P-401 Service Manual",
// "Sodium Hydroxide SDS", external sources "onedrive" / "google_drive"
// / "dropbox" / "s3") were ENTERPRISE-LOOKING fakes that violated the
// "no fabricated production data" rule. They've been removed. The
// Knowledge tab now shows an honest empty state with a deep-link to
// the Library module. Real folder + document trees will be wired
// through the Library module's hooks in a follow-up — tracked as a
// cross-module dependency on the audit doc.

const LIBRARY_FOLDERS: Array<{
  id: string;
  name: string;
  type: 'scope' | 'folder';
  count: number;
  children?: Array<{ id: string; name: string; type: 'folder'; count: number }>;
}> = [];

const LIBRARY_DOCUMENTS: Array<{
  id: string;
  name: string;
  type: string;
  folder: string;
  size: string;
  chunks: number;
  source: string;
}> = [];

const SOURCE_ICONS: Record<string, string> = {
  onedrive: 'OneDrive', google_drive: 'Google Drive', dropbox: 'Dropbox', s3: 'S3', wiki: 'Wiki', uploaded: 'Uploaded',
};

// ── Component ───────────────────────────────────────────────────────────────

interface AgentConfigSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  config: Record<string, unknown>;
  label: string;
  onSave: (config: Record<string, unknown>, label: string) => void;
}

export function AgentConfigSheet({ open, onOpenChange, config, label: initialLabel, onSave }: AgentConfigSheetProps) {
  const [label, setLabel] = useState(initialLabel);
  const [prompt, setPrompt] = useState((config.prompt as string) ?? '');
  const [promptTemplateId, setPromptTemplateId] = useState((config.promptTemplateId as string) ?? '');
  const [promptTemplateCommand, setPromptTemplateCommand] = useState((config.promptTemplateCommand as string) ?? '');
  const [promptTemplateTitle, setPromptTemplateTitle] = useState((config.promptTemplateTitle as string) ?? '');
  const [promptApplyOpen, setPromptApplyOpen] = useState(false);
  const [model, setModel] = useState((config.model as string) ?? 'default');
  const [maxSteps, setMaxSteps] = useState((config.maxSteps as number) ?? 10);
  const [temperature, setTemperature] = useState((config.temperature as number) ?? 0.7);
  const [contextWindowPct, setContextWindowPct] = useState((config.contextWindowPct as number) ?? 80);
  const [includeContext, setIncludeContext] = useState((config.includeContext as boolean) ?? true);
  const [includePrevSteps, setIncludePrevSteps] = useState((config.includePrevSteps as boolean) ?? true);
  const [approvalPolicy, setApprovalPolicy] = useState((config.approvalPolicy as string) ?? 'auto');
  const [outputFormat, setOutputFormat] = useState((config.outputFormat as string) ?? 'text');
  const [selectedTools, setSelectedTools] = useState<Set<string>>(new Set((config.tools as string[]) ?? []));
  const [selectedSkills, setSelectedSkills] = useState<Set<string>>(new Set((config.skills as string[]) ?? []));
  const [selectedDocs, setSelectedDocs] = useState<Set<string>>(new Set((config.knowledgeDocs as string[]) ?? []));
  // #172 — default knowledge selection MUST be empty until the real
  // Library picker is wired. Round-2 PR #186 deleted LIBRARY_FOLDERS
  // but left the legacy default of `['folder-all']` here, so every
  // agent opened the sheet with `Knowledge (1) / 1 folders` while
  // the body showed the empty state — a contradictory UI. Also
  // strip any persisted `folder-all` value so existing saved
  // configs migrate cleanly.
  const [selectedFolders, setSelectedFolders] = useState<Set<string>>(
    () => new Set(
      ((config.knowledgeFolders as string[]) ?? []).filter((id) => id !== 'folder-all'),
    ),
  );
  const [uploadedFiles, setUploadedFiles] = useState<string[]>((config.uploadedFiles as string[]) ?? []);

  const [toolSearch, setToolSearch] = useState('');
  const [skillSearch, setSkillSearch] = useState('');
  const [knowledgeSearch, setKnowledgeSearch] = useState('');
  const [resourceTab, setResourceTab] = useState<'tools' | 'skills' | 'knowledge'>('tools');
  const [knowledgeView, setKnowledgeView] = useState<'folders' | 'documents'>('folders');

  // Model context
  const selectedModel = MODELS.find(m => m.id === model) ?? MODELS[0];
  const effectiveContextTokens = Math.round(selectedModel.maxContext * (contextWindowPct / 100));

  // Live tool catalog from workspace tools registry (built-in + MCP + OpenAPI + custom + DB).
  // Only active tools are shown to the agent picker.
  const { data: toolsData, isLoading: toolsLoading } = useWorkspaceTools({ active: 'true' });
  const {
    data: studioSkillsResp,
    isLoading: skillsLoading,
    isError: skillsError,
  } = useStudioSkills({ activeOnly: true, scope: 'all' });
  const availableTools: AgentToolItem[] = useMemo(() => {
    const rows = toolsData?.data ?? [];
    return rows.map((t) => ({
      id: t.name,
      name: t.displayName || t.name,
      description: t.description || '',
      // Group by tool source so MCP / built-in / custom etc. cluster together.
      group:
        t.toolSource === 'builtin' ? 'Built-in'
        : t.toolSource === 'mcp' ? `MCP${t.namespace ? ` · ${t.namespace}` : ''}`
        : t.toolSource === 'openapi' ? 'OpenAPI'
        : t.toolSource === 'database' ? 'Database'
        : 'Custom',
    }));
  }, [toolsData]);

  const availableSkills: AgentSkillItem[] = useMemo(() => {
    return (studioSkillsResp?.items ?? []).map((skill) => ({
      id: skill.id,
      slug: skill.name,
      name: skill.displayName || skill.name,
      description: skill.description || '',
      scope: skill.scope,
      category: skill.category || '',
      tools: skill.toolDependencies ?? [],
    }));
  }, [studioSkillsResp]);
  const skillSelectionSources: StudioSkillSelectionSource[] = useMemo(
    () =>
      availableSkills.map((skill) => ({
        id: skill.id,
        name: skill.slug,
        displayName: skill.name,
        scope: skill.scope,
      })),
    [availableSkills],
  );

  const filteredTools = useMemo(() => {
    if (!toolSearch) return availableTools;
    const q = toolSearch.toLowerCase();
    return availableTools.filter(t => t.name.toLowerCase().includes(q) || t.id.toLowerCase().includes(q) || t.group.toLowerCase().includes(q) || t.description.toLowerCase().includes(q));
  }, [toolSearch, availableTools]);

  const filteredSkills = useMemo(() => {
    if (!skillSearch) return availableSkills;
    const q = skillSearch.toLowerCase();
    return availableSkills.filter(s =>
      s.name.toLowerCase().includes(q) ||
      s.slug.toLowerCase().includes(q) ||
      s.description.toLowerCase().includes(q) ||
      s.category.toLowerCase().includes(q),
    );
  }, [skillSearch, availableSkills]);

  const filteredDocs = useMemo(() => {
    if (!knowledgeSearch) return LIBRARY_DOCUMENTS;
    const q = knowledgeSearch.toLowerCase();
    return LIBRARY_DOCUMENTS.filter(d => d.name.toLowerCase().includes(q) || d.folder.toLowerCase().includes(q));
  }, [knowledgeSearch]);

  const toolGroups = useMemo(() => {
    const groups: Record<string, AgentToolItem[]> = {};
    for (const tool of filteredTools) {
      if (!groups[tool.group]) groups[tool.group] = [];
      groups[tool.group].push(tool);
    }
    return groups;
  }, [filteredTools]);

  const toggleTool = (id: string) => setSelectedTools(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleSkill = (skill: AgentSkillItem) =>
    setSelectedSkills((prev) =>
      new Set(
        toggleStudioSkillSelection(
          prev,
          {
            id: skill.id,
            name: skill.slug,
            displayName: skill.name,
            scope: skill.scope,
          },
          skillSelectionSources,
        ),
      ),
    );
  const toggleDoc = (id: string) => setSelectedDocs(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleFolder = (id: string) => {
    // #172 — straight set/unset. The previous logic enforced an
    // implicit `folder-all` selection whenever the set was empty,
    // which is why the Knowledge tab always showed `(1)` even
    // after PR #186 deleted the fake folder catalogue. Empty is
    // a valid (and now default) state.
    setSelectedFolders(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const handleUploadFile = () => {
    const name = `workflow-upload-${Date.now()}.pdf`;
    setUploadedFiles(prev => [...prev, name]);
    toast.success('File uploaded for this workflow');
  };

  const handleSave = () => {
    onSave({
      ...config, prompt, model, maxSteps, temperature, contextWindowPct,
      includeContext, includePrevSteps, approvalPolicy, outputFormat,
      tools: Array.from(selectedTools),
      skills: normalizeStudioSkillSelections(selectedSkills, skillSelectionSources),
      ...(promptTemplateId ? { promptTemplateId, promptTemplateCommand, promptTemplateTitle } : {}),
      knowledgeDocs: Array.from(selectedDocs), knowledgeFolders: Array.from(selectedFolders),
      uploadedFiles,
    }, label);
    onOpenChange(false);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-[720px] sm:max-w-[720px] p-0 flex flex-col">
        {/* Header */}
        <SheetHeader className="px-6 py-4 border-b shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
              <Bot className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="flex-1">
              <SheetTitle className="text-base">Configure Agent Node</SheetTitle>
              <Input value={label} onChange={e => setLabel(e.target.value)} className="h-7 text-xs mt-1 max-w-[300px]" placeholder="Agent label..." />
            </div>
            <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
              <Wrench className="h-3 w-3" /> {selectedTools.size}
              <span>·</span>
              <BookOpen className="h-3 w-3" /> {selectedSkills.size}
              <span>·</span>
              <Database className="h-3 w-3" /> {selectedDocs.size + selectedFolders.size}
            </div>
          </div>
        </SheetHeader>

        {/* Two-column body */}
        <div className="flex-1 flex min-h-0 overflow-hidden">
          {/* Left: Settings */}
          <ScrollArea className="w-[320px] border-r shrink-0">
            <div className="p-4 space-y-4">
              {/* Instructions */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-xs font-semibold">Instructions</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-[10px] gap-1"
                    onClick={() => setPromptApplyOpen(true)}
                  >
                    <Sparkles className="h-3 w-3" />
                    Apply Studio prompt
                  </Button>
                </div>
                <Textarea value={prompt} onChange={e => setPrompt(e.target.value)} rows={4} className="text-xs" placeholder="What should this agent do? Be specific about inputs, processing, and expected output format." />
                {promptTemplateTitle && (
                  <p className="text-[9px] text-muted-foreground">
                    Applied from <span className="font-medium">{promptTemplateTitle}</span>
                    {promptTemplateCommand ? <span className="font-mono"> /{promptTemplateCommand}</span> : null}
                  </p>
                )}
              </div>

              {/* Model */}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Model</Label>
                <Select value={model} onValueChange={setModel}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MODELS.map(m => (
                      <SelectItem key={m.id} value={m.id}>
                        <div className="flex items-center gap-2">
                          <span>{m.name}</span>
                          <span className="text-muted-foreground text-[10px]">{m.detail} · {formatTokens(m.maxContext)} ctx</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              {/* Memory & Context Window */}
              <div className="space-y-3">
                <Label className="text-xs font-semibold flex items-center gap-1"><Brain className="h-3 w-3 text-primary" /> Memory & Context</Label>

                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-muted-foreground">Context Window Usage</span>
                    <span className="text-[10px] font-mono font-semibold">{formatTokens(effectiveContextTokens)} / {formatTokens(selectedModel.maxContext)}</span>
                  </div>
                  <Slider min={10} max={100} step={5} value={[contextWindowPct]} onValueChange={([v]) => setContextWindowPct(v)} />
                  <Progress value={contextWindowPct} className={cn('h-1.5', contextWindowPct > 90 ? '[&>div]:bg-red-500' : contextWindowPct > 70 ? '[&>div]:bg-amber-500' : '[&>div]:bg-emerald-500')} />
                  <p className="text-[9px] text-muted-foreground">
                    {contextWindowPct}% of {selectedModel.name}'s {formatTokens(selectedModel.maxContext)} token window.
                    {contextWindowPct > 90 && ' ⚠️ High usage may cause truncation.'}
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Include workflow context</span>
                    <Switch checked={includeContext} onCheckedChange={setIncludeContext} />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Include previous step results</span>
                    <Switch checked={includePrevSteps} onCheckedChange={setIncludePrevSteps} />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Execution */}
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Max Steps: {maxSteps}</Label>
                  <Slider min={1} max={50} step={1} value={[maxSteps]} onValueChange={([v]) => setMaxSteps(v)} />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Temperature: {temperature.toFixed(1)}</Label>
                  <Slider min={0} max={1} step={0.1} value={[temperature]} onValueChange={([v]) => setTemperature(v)} />
                </div>
              </div>

              <Separator />

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Approval Policy</Label>
                <Select value={approvalPolicy} onValueChange={setApprovalPolicy}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto-approve all actions</SelectItem>
                    <SelectItem value="require_writes">Require approval for writes</SelectItem>
                    <SelectItem value="require_all">Require approval for all tools</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Output Format</Label>
                <Select value={outputFormat} onValueChange={setOutputFormat}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Free Text (Markdown)</SelectItem>
                    <SelectItem value="json">Structured JSON</SelectItem>
                    <SelectItem value="report">Report Format</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </ScrollArea>

          {/* Right: Resources */}
          <div className="flex-1 flex flex-col min-w-0">
            <div className="px-4 pt-3 pb-2 border-b shrink-0">
              <Tabs value={resourceTab} onValueChange={v => setResourceTab(v as typeof resourceTab)}>
                <TabsList className="h-8">
                  <TabsTrigger value="tools" className="text-xs gap-1" title={`${selectedTools.size} of ${availableTools.length} tools selected`}><Wrench className="h-3 w-3" /> Tools ({selectedTools.size}/{availableTools.length})</TabsTrigger>
                  <TabsTrigger value="skills" className="text-xs gap-1" title={`${selectedSkills.size} of ${availableSkills.length} skills selected`}><BookOpen className="h-3 w-3" /> Skills ({selectedSkills.size}/{availableSkills.length})</TabsTrigger>
                  <TabsTrigger value="knowledge" className="text-xs gap-1" title={`${selectedFolders.size + selectedDocs.size} knowledge sources selected`}><Database className="h-3 w-3" /> Knowledge ({selectedFolders.size + selectedDocs.size})</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            <ScrollArea className="flex-1">
              {/* ═══ Tools ═══ */}
              {resourceTab === 'tools' && (
                <div className="p-3 space-y-3">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input value={toolSearch} onChange={e => setToolSearch(e.target.value)} placeholder="Search tools..." className="h-8 text-xs pl-8" />
                  </div>
                  {Object.entries(toolGroups).map(([group, tools]) => (
                    <div key={group}>
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">{group}</p>
                      <div className="space-y-0.5">
                        {tools.map(tool => (
                          <label key={tool.id} className={cn('flex items-start gap-2 p-2 rounded-lg cursor-pointer transition-colors', selectedTools.has(tool.id) ? 'bg-primary/5 border border-primary/20' : 'hover:bg-muted/50 border border-transparent')}>
                            <Checkbox checked={selectedTools.has(tool.id)} onCheckedChange={() => toggleTool(tool.id)} className="mt-0.5" />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs font-medium">{tool.name}</span>
                                <span className="text-[8px] font-mono text-muted-foreground/50">{tool.id}</span>
                              </div>
                              <p className="text-[10px] text-muted-foreground">{tool.description}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                  {!toolsLoading && availableTools.length === 0 && (
                    <div className="border border-dashed rounded-lg p-6 flex flex-col items-center justify-center text-center">
                      <Wrench className="h-6 w-6 text-muted-foreground/30 mb-2" />
                      <p className="text-xs font-medium text-muted-foreground">No tools available</p>
                      <p className="text-[10px] text-muted-foreground/70 mt-1 max-w-[260px]">
                        Connect a system to give this agent tools to call.
                      </p>
                      <Link
                        to="/connections/marketplace"
                        className="mt-3 text-[11px] text-primary hover:underline"
                      >
                        Connect a system →
                      </Link>
                    </div>
                  )}
                  {toolsLoading && availableTools.length === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-6">Loading tools…</p>
                  )}
                  {availableTools.length > 0 && filteredTools.length === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-6">No tools match "{toolSearch}"</p>
                  )}
                </div>
              )}

              {/* ═══ Skills ═══ */}
              {resourceTab === 'skills' && (
                <div className="p-3 space-y-3">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input value={skillSearch} onChange={e => setSkillSearch(e.target.value)} placeholder="Search skills..." className="h-8 text-xs pl-8" />
                  </div>
                  <div className="space-y-1">
                    {filteredSkills.map(skill => {
                      const selected = isStudioSkillSelected(
                        selectedSkills,
                        {
                          id: skill.id,
                          name: skill.slug,
                          displayName: skill.name,
                          scope: skill.scope,
                        },
                        skillSelectionSources,
                      );
                      return (
                        <label key={`${skill.scope}:${skill.id}`} className={cn('flex items-start gap-2 p-2.5 rounded-lg cursor-pointer transition-colors', selected ? 'bg-purple-500/5 border border-purple-500/20' : 'hover:bg-muted/50 border border-transparent')}>
                          <Checkbox checked={selected} onCheckedChange={() => toggleSkill(skill)} className="mt-0.5" />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs font-medium">{skill.name}</span>
                              <Badge variant="outline" className="text-[8px] h-4 px-1">{skill.scope === 'system' ? 'System' : 'Tenant'}</Badge>
                            </div>
                            <p className="text-[9px] font-mono text-muted-foreground/70">{skill.slug}</p>
                            <p className="text-[10px] text-muted-foreground mt-0.5">{skill.description}</p>
                            {skill.tools.length > 0 && <div className="flex items-center gap-1 mt-1 flex-wrap">
                              <Wrench className="h-2.5 w-2.5 text-muted-foreground" />
                              {skill.tools.map(t => <Badge key={t} variant="secondary" className="text-[8px] h-4 px-1 font-mono">{t.split('.').pop()}</Badge>)}
                            </div>}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                  {skillsError && (
                    <div className="border border-destructive/30 rounded-lg p-6 flex flex-col items-center justify-center text-center">
                      <BookOpen className="h-6 w-6 text-destructive/60 mb-2" />
                      <p className="text-xs font-medium text-destructive">Studio skills could not be loaded</p>
                      <p className="text-[10px] text-muted-foreground/70 mt-1 max-w-[260px]">
                        Refresh this sheet or open Studio Skills to verify the catalog before saving skill changes.
                      </p>
                      <Link to="/studio/skills" className="mt-3 text-[11px] text-primary hover:underline">Open Studio Skills</Link>
                    </div>
                  )}
                  {!skillsError && !skillsLoading && availableSkills.length === 0 && (
                    <div className="border border-dashed rounded-lg p-6 flex flex-col items-center justify-center text-center">
                      <BookOpen className="h-6 w-6 text-muted-foreground/30 mb-2" />
                      <p className="text-xs font-medium text-muted-foreground">No Studio skills available</p>
                      <Link to="/studio/skills" className="mt-3 text-[11px] text-primary hover:underline">Open Studio Skills</Link>
                    </div>
                  )}
                  {!skillsError && skillsLoading && availableSkills.length === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-6">Loading Studio skills...</p>
                  )}
                  {availableSkills.length > 0 && filteredSkills.length === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-6">No skills match "{skillSearch}"</p>
                  )}
                </div>
              )}

              {/* ═══ Knowledge ═══ */}
              {resourceTab === 'knowledge' && (
                <div className="p-3 space-y-3">
                  {/* Toggle: Folders / Documents */}
                  <div className="flex items-center gap-2">
                    <Button variant={knowledgeView === 'folders' ? 'default' : 'outline'} size="sm" className="h-7 text-xs gap-1" onClick={() => setKnowledgeView('folders')}>
                      <Folder className="h-3 w-3" /> Folders
                    </Button>
                    <Button variant={knowledgeView === 'documents' ? 'default' : 'outline'} size="sm" className="h-7 text-xs gap-1" onClick={() => setKnowledgeView('documents')}>
                      <FileText className="h-3 w-3" /> Documents
                    </Button>
                    <div className="flex-1" />
                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={handleUploadFile} title="Upload a file for this workflow only">
                      <Upload className="h-3 w-3" /> Upload
                    </Button>
                  </div>

                  {/* Search */}
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input value={knowledgeSearch} onChange={e => setKnowledgeSearch(e.target.value)} placeholder={knowledgeView === 'folders' ? 'Search folders...' : 'Search documents by name or folder...'} className="h-8 text-xs pl-8" />
                  </div>

                  {/* Uploaded files for this workflow */}
                  {uploadedFiles.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Workflow-Specific Uploads</p>
                      {uploadedFiles.map((f, i) => (
                        <div key={i} className="flex items-center gap-2 bg-primary/5 border border-primary/20 rounded-lg px-2.5 py-1.5">
                          <Upload className="h-3 w-3 text-primary shrink-0" />
                          <span className="text-xs flex-1 truncate">{f}</span>
                          <Button variant="ghost" size="icon" className="h-5 w-5 text-destructive" onClick={() => setUploadedFiles(prev => prev.filter((_, j) => j !== i))} aria-label={`Remove file '${f}'`} title="Remove">
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Folders view */}
                  {knowledgeView === 'folders' && (
                    <div className="space-y-1">
                      {LIBRARY_FOLDERS.length > 0 ? (
                        <>
                          <p className="text-[10px] text-muted-foreground mb-1">Select folders the agent can search. "All Documents" includes everything.</p>
                          {LIBRARY_FOLDERS.map(folder => (
                            <div key={folder.id}>
                              <label className={cn('flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors', selectedFolders.has(folder.id) ? 'bg-blue-500/5 border border-blue-500/20' : 'hover:bg-muted/50 border border-transparent')}>
                                <Checkbox checked={selectedFolders.has(folder.id)} onCheckedChange={() => toggleFolder(folder.id)} />
                                {folder.id === 'folder-all' ? <Database className="h-3.5 w-3.5 text-primary shrink-0" /> : <Folder className="h-3.5 w-3.5 text-amber-500 shrink-0" />}
                                <span className="text-xs font-medium flex-1">{folder.name}</span>
                                <Badge variant="secondary" className="text-[8px]">{folder.count} docs</Badge>
                              </label>
                              {folder.children && (
                                <div className="ml-6 space-y-0.5 mt-0.5">
                                  {folder.children.map((child) => (
                                    <label key={child.id} className={cn('flex items-center gap-2 p-1.5 rounded cursor-pointer transition-colors', selectedFolders.has(child.id) ? 'bg-blue-500/5' : 'hover:bg-muted/30')}>
                                      <Checkbox checked={selectedFolders.has(child.id)} onCheckedChange={() => toggleFolder(child.id)} />
                                      <FolderOpen className="h-3 w-3 text-amber-500/60 shrink-0" />
                                      <span className="text-[10px] flex-1">{child.name}</span>
                                      <span className="text-[9px] text-muted-foreground">{child.count}</span>
                                    </label>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </>
                      ) : (
                        // #172 — honest empty state until the Library hook is wired
                        <div className="border border-dashed rounded-md p-4 text-center">
                          <Folder className="h-5 w-5 mx-auto text-muted-foreground/30 mb-2" />
                          <p className="text-xs font-medium">No knowledge folders available</p>
                          <p className="text-[10px] text-muted-foreground mt-1 max-w-[280px] mx-auto">
                            Workflow agents currently can't browse the tenant's Library tree from this sheet. Add documents in Library and we'll wire the picker in a follow-up.
                          </p>
                          <Link
                            to="/library"
                            className="mt-3 inline-block text-[10px] text-primary hover:underline"
                          >
                            Open Library →
                          </Link>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Documents view */}
                  {knowledgeView === 'documents' && (
                    <div className="space-y-0.5">
                      {LIBRARY_DOCUMENTS.length > 0 ? (
                        <>
                          <p className="text-[10px] text-muted-foreground mb-1">Select specific documents the agent can access.</p>
                          {filteredDocs.map(doc => (
                            <label key={doc.id} className={cn('flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors', selectedDocs.has(doc.id) ? 'bg-blue-500/5 border border-blue-500/20' : 'hover:bg-muted/50 border border-transparent')}>
                              <Checkbox checked={selectedDocs.has(doc.id)} onCheckedChange={() => toggleDoc(doc.id)} />
                              <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-medium truncate">{doc.name}</p>
                                <div className="flex items-center gap-2 text-[9px] text-muted-foreground">
                                  <span>{doc.folder}</span>
                                  <span>·</span>
                                  <span>{doc.size}</span>
                                  <span>·</span>
                                  <span>{doc.chunks} chunks</span>
                                  <span>·</span>
                                  <Badge variant="secondary" className="text-[7px] h-3 px-1">{SOURCE_ICONS[doc.source] ?? doc.source}</Badge>
                                </div>
                              </div>
                            </label>
                          ))}
                          {filteredDocs.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">No documents match "{knowledgeSearch}"</p>}
                        </>
                      ) : (
                        // #172 — honest empty state until the Library hook is wired
                        <div className="border border-dashed rounded-md p-4 text-center">
                          <FileText className="h-5 w-5 mx-auto text-muted-foreground/30 mb-2" />
                          <p className="text-xs font-medium">No documents available</p>
                          <p className="text-[10px] text-muted-foreground mt-1 max-w-[280px] mx-auto">
                            The per-document picker will use the tenant's real Library inventory once the Library hook is wired into Automate.
                          </p>
                          <Link
                            to="/library"
                            className="mt-3 inline-block text-[10px] text-primary hover:underline"
                          >
                            Open Library →
                          </Link>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t flex items-center justify-between shrink-0">
          <div className="text-[10px] text-muted-foreground">
            {selectedTools.size} tools · {selectedSkills.size} skills · {selectedFolders.size} folders · {selectedDocs.size} docs · {formatTokens(effectiveContextTokens)} context
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button size="sm" onClick={handleSave} className="gap-1">
              <Check className="h-3.5 w-3.5" /> Apply Configuration
            </Button>
          </div>
        </div>
        </SheetContent>
      </Sheet>
      <PromptApplyDialog
        open={promptApplyOpen}
        onOpenChange={setPromptApplyOpen}
        applyLabel="Use as instructions"
        onApply={(text, source) => {
          setPrompt(text);
          setPromptTemplateId(source.id);
          setPromptTemplateCommand(source.command);
          setPromptTemplateTitle(source.title);
        }}
      />
    </>
  );
}
